import React from 'react';
import { SEVERITY_ORDER, SEVERITY_COLORS } from '../utils/symptomHelpers';

// ── Bucket building ───────────────────────────────────────────────────────────

function avgScore(entries) {
  if (!entries.length) return 0;
  return (
    entries.reduce((sum, s) => sum + (SEVERITY_ORDER[s.severity] || 0), 0) /
    entries.length
  );
}

function maxSevKey(entries) {
  if (!entries.length) return null;
  return entries.reduce(
    (max, s) =>
      (SEVERITY_ORDER[s.severity] || 0) > (SEVERITY_ORDER[max] || 0)
        ? s.severity
        : max,
    entries[0].severity
  );
}

function buildBuckets(symptoms, range) {
  const now = new Date();

  if (range === 'daily') {
    // 6 buckets of 4 hours covering the last 24 h
    return Array.from({ length: 6 }, (_, i) => {
      const blockEnd = new Date(now);
      blockEnd.setHours(blockEnd.getHours() - i * 4, 0, 0, 0);
      const blockStart = new Date(blockEnd);
      blockStart.setHours(blockStart.getHours() - 4);

      const entries = symptoms.filter(s => {
        const t = new Date(s.loggedAt);
        return t >= blockStart && t < blockEnd;
      });

      const h = blockEnd.getHours();
      const label =
        h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;

      return { label, entries, score: avgScore(entries), maxSev: maxSevKey(entries) };
    }).reverse();
  }

  if (range === 'weekly') {
    // 7 buckets — one per day for the last 7 days
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() - (6 - i));
      const dayStr = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
      const label = d.toLocaleDateString('en-US', { weekday: 'short' });

      const entries = symptoms.filter(
        s => new Date(s.loggedAt).toLocaleDateString('en-CA') === dayStr
      );

      return { label, entries, score: avgScore(entries), maxSev: maxSevKey(entries) };
    });
  }

  // monthly — 4 buckets, one per week
  return Array.from({ length: 4 }, (_, i) => {
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() - i * 7);
    weekEnd.setHours(23, 59, 59, 999);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);
    weekStart.setHours(0, 0, 0, 0);

    const entries = symptoms.filter(s => {
      const t = new Date(s.loggedAt);
      return t >= weekStart && t <= weekEnd;
    });

    return {
      label: `Wk ${4 - i}`,
      entries,
      score: avgScore(entries),
      maxSev: maxSevKey(entries),
    };
  }).reverse();
}

// ── Chart component ───────────────────────────────────────────────────────────

const SVG_W = 560;
const SVG_H = 160;
const PAD = { left: 10, right: 10, top: 14, bottom: 28 };
const innerW = SVG_W - PAD.left - PAD.right;
const innerH = SVG_H - PAD.top - PAD.bottom;

export default function SymptomTrendChart({ symptoms, range }) {
  if (!symptoms || symptoms.length === 0) {
    return (
      <div
        style={{
          height: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#718096',
          fontSize: '14px',
        }}
      >
        No symptom data for this period
      </div>
    );
  }

  const buckets = buildBuckets(symptoms, range);
  const n = buckets.length;
  const bfw = innerW / n; // full slot width per bucket
  const bw = Math.max(bfw * 0.58, 6); // bar width
  const bo = (bfw - bw) / 2; // centering offset

  const barX = i => PAD.left + i * bfw + bo;
  const barH = score => (score > 0 ? Math.max((score / 4) * innerH, 4) : 1);
  const barY = score => PAD.top + innerH - barH(score);
  const barColor = b => (b.maxSev ? SEVERITY_COLORS[b.maxSev] : '#e2e8f0');
  const labelX = i => PAD.left + i * bfw + bfw / 2;

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label="Symptom severity trend chart"
    >
      {/* Baseline */}
      <line
        x1={PAD.left}
        x2={PAD.left + innerW}
        y1={PAD.top + innerH}
        y2={PAD.top + innerH}
        stroke="#e2e8f0"
        strokeWidth={1}
      />

      {/* Horizontal grid lines at each severity level */}
      {[1, 2, 3, 4].map(score => (
        <line
          key={score}
          x1={PAD.left}
          x2={PAD.left + innerW}
          y1={barY(score)}
          y2={barY(score)}
          stroke="#f1f5f9"
          strokeWidth={1}
        />
      ))}

      {/* Bars */}
      {buckets.map((b, i) => (
        <rect
          key={i}
          x={barX(i)}
          y={barY(b.score)}
          width={bw}
          height={barH(b.score)}
          rx={3}
          fill={barColor(b)}
          fillOpacity={b.score > 0 ? 0.9 : 0.25}
        />
      ))}

      {/* X-axis labels */}
      {buckets.map((b, i) => (
        <text
          key={i}
          x={labelX(i)}
          y={PAD.top + innerH + 18}
          fontSize={10}
          textAnchor="middle"
          fill="#718096"
        >
          {b.label}
        </text>
      ))}
    </svg>
  );
}
